import type { Config } from "@puckeditor/core";
import type { HeroProps } from "../components/hero/hero";
import type { CTAProps } from "../components/cta/cta";
import type { ServicesProps } from "../components/services/services";
import type { ReviewsProps } from "../components/reviews/reviews";
import type { ContactFormProps } from "../components/contact-form/contact-form";
import type { FormBlockProps } from "../components/form-block/form-block";
import type { BookingWidgetProps } from "../components/booking-widget/booking-widget";
import type { ContentBlockProps } from "../components/content-block/content-block";
import type { ProcessStepsProps } from "../components/process-steps/process-steps";
import type { FAQAccordionProps } from "../components/faq-accordion/faq-accordion";
import type { ServiceAreasProps } from "../components/service-areas/service-areas";
import type { BreadcrumbProps } from "../components/breadcrumb/breadcrumb";
import type { BenefitsListProps } from "../components/benefits-list/benefits-list";
import type { PricingInfoProps } from "../components/pricing-info/pricing-info";
import type { MobileStickyCallProps } from "../components/mobile-sticky-call/mobile-sticky-call";
import type { GalleryProps } from "../components/gallery/gallery";
import type { LinksPageProps } from "../components/links-page/links-page";
import type { BeforeAfterProps } from "../components/before-after/before-after";
import type { BrandMarqueeProps } from "../components/brand-marquee/brand-marquee";
import type { EmergencyBandProps } from "../components/emergency-band/emergency-band";
import type {
  BadgeBlockProps,
  ButtonBlockProps,
  DividerBlockProps,
  GroupBlockProps,
  HeadingBlockProps,
  ImageBlockProps,
  SectionBlockProps,
  SpacerBlockProps,
  TextBlockProps,
} from "../components/primitives/primitives";
import { HeroFields } from "../components/hero/hero.fields";
import { CTAFields } from "../components/cta/cta.fields";
import { ServicesFields } from "../components/services/services.fields";
import { ReviewsFields } from "../components/reviews/reviews.fields";
import { ContactFormFields } from "../components/contact-form/contact-form.fields";
import { FormBlockFields } from "../components/form-block/form-block.fields";
import { BookingWidgetFields } from "../components/booking-widget/booking-widget.fields";
import { ContentBlockFields } from "../components/content-block/content-block.fields";
import { NavigationFields } from "../components/navigation/navigation.fields";
import { FooterFields } from "../components/footer/footer.fields";
import { ProcessStepsFields } from "../components/process-steps/process-steps.fields";
import { FAQAccordionFields } from "../components/faq-accordion/faq-accordion.fields";
import { ServiceAreasFields } from "../components/service-areas/service-areas.fields";
import { BreadcrumbFields } from "../components/breadcrumb/breadcrumb.fields";
import { BenefitsListFields } from "../components/benefits-list/benefits-list.fields";
import { PricingInfoFields } from "../components/pricing-info/pricing-info.fields";
import { MobileStickyCallFields } from "../components/mobile-sticky-call/mobile-sticky-call.fields";
import { GalleryFields } from "../components/gallery/gallery.fields";
import { LinksPageFields } from "../components/links-page/links-page.fields";
import { LinksPageConfig } from "../components/links-page/links-page.config";
import { BeforeAfterFields } from "../components/before-after/before-after.fields";
import { BeforeAfterConfig } from "../components/before-after/before-after.config";
import { EmergencyBandFields } from "../components/emergency-band/emergency-band.fields";
import { EmergencyBandConfig } from "../components/emergency-band/emergency-band.config";
import { BrandMarqueeFields } from "../components/brand-marquee/brand-marquee.fields";
import {
  BadgeBlockFields,
  ButtonBlockFields,
  DividerBlockFields,
  GroupBlockFields,
  HeadingBlockFields,
  ImageBlockFields,
  SectionBlockFields,
  SpacerBlockFields,
  TextBlockFields,
} from "../components/primitives/primitives.fields";
import {
  BadgeBlockConfig,
  ButtonBlockConfig,
  DividerBlockConfig,
  GroupBlockConfig,
  HeadingBlockConfig,
  ImageBlockConfig,
  SectionBlockConfig,
  SpacerBlockConfig,
  TextBlockConfig,
} from "../components/primitives/primitives.config";
import { HeroConfig } from "../components/hero/hero.config";
import { CTAConfig } from "../components/cta/cta.config";
import { ServicesConfig } from "../components/services/services.config";
import { ReviewsConfig } from "../components/reviews/reviews.config";
import { ContactFormConfig } from "../components/contact-form/contact-form.config";
import { FormBlockConfig } from "../components/form-block/form-block.config";
import { BookingWidgetConfig } from "../components/booking-widget/booking-widget.config";
import { ContentBlockConfig } from "../components/content-block/content-block.config";
import { ProcessStepsConfig } from "../components/process-steps/process-steps.config";
import { FAQAccordionConfig } from "../components/faq-accordion/faq-accordion.config";
import { ServiceAreasConfig } from "../components/service-areas/service-areas.config";
import { BreadcrumbConfig } from "../components/breadcrumb/breadcrumb.config";
import { BenefitsListConfig } from "../components/benefits-list/benefits-list.config";
import { PricingInfoConfig } from "../components/pricing-info/pricing-info.config";
import { MobileStickyCallConfig } from "../components/mobile-sticky-call/mobile-sticky-call.config";
import { GalleryConfig } from "../components/gallery/gallery.config";
import { BrandMarqueeConfig } from "../components/brand-marquee/brand-marquee.config";

interface RegisteredJobsiteComponent {
  fields: Record<string, unknown>;
  editorConfig?: unknown;
  /**
   * Billing feature slug required to ADD this component to a page. The entry
   * declares it; pickers show the lock and the save path enforces it. Absent
   * means available on every plan. Moving a component in or out of a gate is
   * this one line.
   */
  requiredFeature?: string;
}

type RegisteredJobsiteComponentMap = Record<string, RegisteredJobsiteComponent>;

type FieldRegistryFrom<T extends RegisteredJobsiteComponentMap> = {
  [K in keyof T]: T[K]["fields"];
};

type EditorRegistryFrom<T extends RegisteredJobsiteComponentMap> = {
  [K in keyof T as T[K] extends { editorConfig: unknown } ? K : never]: T[K] extends {
    editorConfig: infer C;
  }
    ? NonNullable<C>
    : never;
};

export interface JobsitePageComponentProps {
  Hero: HeroProps;
  CTA: CTAProps;
  Services: ServicesProps;
  Reviews: ReviewsProps;
  ContactForm: ContactFormProps;
  FormBlock: FormBlockProps;
  BookingWidget: BookingWidgetProps;
  ContentBlock: ContentBlockProps;
  ProcessSteps: ProcessStepsProps;
  FAQAccordion: FAQAccordionProps;
  ServiceAreas: ServiceAreasProps;
  Breadcrumb: BreadcrumbProps;
  BenefitsList: BenefitsListProps;
  PricingInfo: PricingInfoProps;
  MobileStickyCall: MobileStickyCallProps;
  Gallery: GalleryProps;
  LinksPage: LinksPageProps;
  BeforeAfter: BeforeAfterProps;
  BrandMarquee: BrandMarqueeProps;
  // Registered with an editorConfig like the rest; it was missing here, which
  // kept it out of every keyed view of the config (categories included).
  EmergencyBand: EmergencyBandProps;
  HeadingBlock: HeadingBlockProps;
  TextBlock: TextBlockProps;
  ButtonBlock: ButtonBlockProps;
  ImageBlock: ImageBlockProps;
  SpacerBlock: SpacerBlockProps;
  DividerBlock: DividerBlockProps;
  SectionBlock: SectionBlockProps;
  BadgeBlock: BadgeBlockProps;
  GroupBlock: GroupBlockProps;
}

function buildFieldRegistry<T extends RegisteredJobsiteComponentMap>(
  registry: T,
): FieldRegistryFrom<T> {
  return Object.fromEntries(
    Object.entries(registry).map(([name, definition]) => [name, definition.fields]),
  ) as FieldRegistryFrom<T>;
}

function buildEditorComponentRegistry<T extends RegisteredJobsiteComponentMap>(
  registry: T,
): EditorRegistryFrom<T> {
  return Object.fromEntries(
    Object.entries(registry)
      .filter(([, definition]) => definition.editorConfig !== undefined)
      .map(([name, definition]) => [name, definition.editorConfig]),
  ) as EditorRegistryFrom<T>;
}

/**
 * Authoritative registration surface for jobsite components.
 *
 * Fields drive generation, hydration, validation, and AI/editor introspection.
 * Optional editorConfig marks components that should appear in the Puck editor.
 */
export const JobsiteComponentRegistry = {
  Hero: { fields: HeroFields, editorConfig: HeroConfig },
  CTA: { fields: CTAFields, editorConfig: CTAConfig },
  Services: { fields: ServicesFields, editorConfig: ServicesConfig },
  Reviews: { fields: ReviewsFields, editorConfig: ReviewsConfig },
  ContactForm: { fields: ContactFormFields, editorConfig: ContactFormConfig },
  FormBlock: { fields: FormBlockFields, editorConfig: FormBlockConfig },
  BookingWidget: { fields: BookingWidgetFields, editorConfig: BookingWidgetConfig },
  ContentBlock: { fields: ContentBlockFields, editorConfig: ContentBlockConfig },
  Navigation: { fields: NavigationFields },
  Footer: { fields: FooterFields },
  ProcessSteps: { fields: ProcessStepsFields, editorConfig: ProcessStepsConfig },
  FAQAccordion: { fields: FAQAccordionFields, editorConfig: FAQAccordionConfig },
  ServiceAreas: { fields: ServiceAreasFields, editorConfig: ServiceAreasConfig },
  Breadcrumb: { fields: BreadcrumbFields, editorConfig: BreadcrumbConfig },
  BenefitsList: { fields: BenefitsListFields, editorConfig: BenefitsListConfig },
  PricingInfo: { fields: PricingInfoFields, editorConfig: PricingInfoConfig },
  MobileStickyCall: {
    fields: MobileStickyCallFields,
    editorConfig: MobileStickyCallConfig,
  },
  Gallery: { fields: GalleryFields, editorConfig: GalleryConfig },
  // The links page's single component. Registered so it renders in the editor
  // and the static build, but hidden from every picker (puck-config category
  // + Simple UI exclusion) — it exists only on the /links page kind, whose
  // permissions lock structure editing.
  LinksPage: { fields: LinksPageFields, editorConfig: LinksPageConfig },
  // Comparison slider gated like the Gallery's old before-after mode was:
  // paid plans add it, existing placements stay editable on any plan.
  BeforeAfter: {
    fields: BeforeAfterFields,
    editorConfig: BeforeAfterConfig,
    requiredFeature: "advanced_layout_editing",
  },
  BrandMarquee: { fields: BrandMarqueeFields, editorConfig: BrandMarqueeConfig },
  EmergencyBand: { fields: EmergencyBandFields, editorConfig: EmergencyBandConfig },

  // Atomic primitives: the pieces to build a layout the opinionated sections
  // don't cover. Freeform building is the advanced-editor tier, mirroring the
  // form builder's standard-free / custom-paid line.
  HeadingBlock: {
    fields: HeadingBlockFields,
    editorConfig: HeadingBlockConfig,
    requiredFeature: "advanced_layout_editing",
  },
  TextBlock: {
    fields: TextBlockFields,
    editorConfig: TextBlockConfig,
    requiredFeature: "advanced_layout_editing",
  },
  ButtonBlock: {
    fields: ButtonBlockFields,
    editorConfig: ButtonBlockConfig,
    requiredFeature: "advanced_layout_editing",
  },
  ImageBlock: {
    fields: ImageBlockFields,
    editorConfig: ImageBlockConfig,
    requiredFeature: "advanced_layout_editing",
  },
  SpacerBlock: {
    fields: SpacerBlockFields,
    editorConfig: SpacerBlockConfig,
    requiredFeature: "advanced_layout_editing",
  },
  DividerBlock: {
    fields: DividerBlockFields,
    editorConfig: DividerBlockConfig,
    requiredFeature: "advanced_layout_editing",
  },
  SectionBlock: {
    fields: SectionBlockFields,
    editorConfig: SectionBlockConfig,
    requiredFeature: "advanced_layout_editing",
  },
  BadgeBlock: {
    fields: BadgeBlockFields,
    editorConfig: BadgeBlockConfig,
    requiredFeature: "advanced_layout_editing",
  },
  GroupBlock: {
    fields: GroupBlockFields,
    editorConfig: GroupBlockConfig,
    requiredFeature: "advanced_layout_editing",
  },
} as const satisfies RegisteredJobsiteComponentMap;

/** Feature slug a component needs before it can be added to a page, or null. */
export function componentRequiredFeature(type: string): string | null {
  const entry = (JobsiteComponentRegistry as RegisteredJobsiteComponentMap)[type];
  return entry?.requiredFeature ?? null;
}

/**
 * Backward-compatible field registry used by AI harnesses and schema extraction.
 */
export const ComponentRegistry = buildFieldRegistry(JobsiteComponentRegistry);

/**
 * Backward-compatible alias consumed by hydration and generation code.
 */
export const AllComponentFields = ComponentRegistry;

/**
 * Puck editor components derived from the authoritative registry.
 * Utility-only editor components like ComponentRef stay separate.
 */
export const PageEditorComponentRegistry = buildEditorComponentRegistry(
  JobsiteComponentRegistry,
) as Config<JobsitePageComponentProps, Record<string, never>>["components"];

export type ComponentName = keyof typeof ComponentRegistry;
export type JobsiteComponentName = keyof typeof JobsiteComponentRegistry;
